import React, { ChangeEvent, useCallback, useState, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useApolloClient, useMutation } from '@apollo/client';
import { useDocumentState } from '../../../../../../contexts/DocumentContext';
import { useNavigate } from 'react-router-dom';
import pull from 'lodash/pull';
import { Col, Dropdown, Row } from 'react-bootstrap';
import { useAuth } from '../../../../../../auth';
import RequestApprovalSlideout from './RequestApprovalSlideout';
import PillToggle from '../../../../../toggle';
import TeamMembersDropdown from './TeamMembersDropdown';
import ApprovalOrderList from './ApprovalOrderList';
import {
  RequestApproval,
  requestApprovalSuccessMessage,
  requestReviewSuccessMessage,
  RequestReviewThresholdValues,
  SetApprovalRequirement,
  PermitEditingDuringApproval,
} from './RequestInternalApprovalModalConstants';
import Toggle from '../../../../../../forms/components/Toggle';
import { createDocumentApprovalRequest } from '../../../../../../utils/api/request-review-mutations/createDocumentApprovalRequest';
import { useDeal } from '../../../../../../contexts/DealContext';
import { paths, fillPlaceholders } from '../../../../../../routing';
import styles from './RequestReview.module.scss';
import { useNotifications } from '../../../../../../notifications';
import { DocumentSourceEnum } from '../../../../document-editor/constants/EditorConstats';
import { saveDocumentVersionOnPossessionPass } from '../../../document-wizard-header.utils';
import { useDocumentRole } from '../../../../../../shared/hooks/useDocumentRole/useDocumentRole';
import { mutations } from '../../../../../../api';
import { Warning } from '../../../../../shared/warning';
import { createDocumentViewRequest } from '../../../../../../utils/api/request-review-mutations/createDocumentViewRequest';
import PlaceholderWarningModal from '../../../../document-wizard-modal/custom-modals/placeholderWarningModal/placeholderWarningModal';

type CreateDocumentModalComponentProps = {
  isOpen: boolean;
  documentId: string;
  teamId: string;
  onClose: () => void;
  dealMembers: any[];
  teamMembersWithApprovePermission: any[];
  handleSaveDocument?: (() => Promise<void>) | undefined;
  isReviewOnly: boolean;
  placeholdersToComplete?: number;
};

const RequestInternalApprovalModalContainer = ({
  isOpen,
  onClose,
  dealMembers,
  teamMembersWithApprovePermission = [],
  documentId,
  handleSaveDocument,
  teamId,
  isReviewOnly,
  placeholdersToComplete = 0,
}: CreateDocumentModalComponentProps) => {
  const { dealId } = useDeal();
  const { state } = useDocumentState();
  const documentSource = state.source;
  const currentDocument = state.document;
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const [{ user }] = useAuth();
  const { formatMessage } = useIntl();
  const { possessionTeamName } = useDocumentRole(documentId);
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<string[]>([]);
  const [isFullApprovementRequired, setIsFullApprovementRequired] = useState(false);
  const [isRequestApproval, setIsRequestApproval] = useState(false);
  const [isEditingPermittedDuringApproval, setIsEditingPermittedDuringApproval] = useState(false);
  const [useApprovalOrder, setUseApprovalOrder] = useState(false);
  const [orderedApprovers, setOrderedApprovers] = useState<any[]>([]);
  const [unlockDocument] = useMutation(mutations.unlockDocument);
  const optionalMessageInputRef = useRef<HTMLTextAreaElement>(null);

  // Create deal URL for navigation
  const dealUrl = paths.deals.detail.replace(':teamId', teamId as string).replace(':dealId', dealId);

  const onApproveSuccess = useCallback(async (isRequestApproval: boolean) => {
    // Unlock the document after successful approval request creation
    try {
      await unlockDocument({ variables: { documentId, userId: user.id, sessionActivities: [] } });
    } catch (error) {
      // Silently fail - unlock failure is non-critical
    }

    // Navigate to deal detail page (similar to send flow)
    navigate(dealUrl);

    // Show success notification after navigation
    setTimeout(() => {
      notifySuccess({
        message: isRequestApproval
          ? formatMessage(requestApprovalSuccessMessage)
          : formatMessage(requestReviewSuccessMessage),
      });
    }, 100);
  }, [unlockDocument, documentId, user.id, navigate, dealUrl, notifySuccess, formatMessage]);

  const onSubmit = useCallback(async () => {
    const isAppDocument = documentSource === DocumentSourceEnum.APP;
    const additionalMessage = optionalMessageInputRef.current?.value;

    // Save document BEFORE creating approval request
    // This prevents the "document cannot be edited" error since the document gets locked after approval request creation
    if (isAppDocument) {
      try {
        handleSaveDocument && currentDocument?.body && (await handleSaveDocument());
        // Skip creating revision for internal reviews - the backend creates it with full content
        // Only create revision for external reviews/sharing
        if (!isRequestApproval) {
          await saveDocumentVersionOnPossessionPass({
            success: true,
            possessionTeamName,
            isInternalReview: false,
          });
        }
      } catch (error) {
        // Show error but allow approval request to continue
        notifyError({
          message: 'Document saved but there was an issue. Approval request will continue.',
        });
      }
    }

    if (isRequestApproval) {
      const variables = {
        dealId,
        documentId,
        teamId,
        threshold: isFullApprovementRequired ? RequestReviewThresholdValues.ALL : RequestReviewThresholdValues.ONE,
        userIds: selectedTeamMembers,
        confirm: true,
        permitEditingDuringApproval: isEditingPermittedDuringApproval,
        additionalMessage,
        useApprovalOrder,
        approvalOrderUserIds: useApprovalOrder ? orderedApprovers.map((a) => a.id) : null,
      };

      try {
        await createDocumentApprovalRequest(variables)(apolloClient);
        await onApproveSuccess(isRequestApproval);
      } catch (error) {
        notifyError({
          message: error,
        });
      }
    } else {
      const variables = {
        dealId,
        documentId,
        viewerIds: selectedTeamMembers,
        additionalMessage,
      };

      try {
        await createDocumentViewRequest(variables)(apolloClient);
        await onApproveSuccess(isRequestApproval);
      } catch (error) {
        notifyError({
          message: error,
        });
      }
    }
  }, [
    isRequestApproval,
    isFullApprovementRequired,
    isEditingPermittedDuringApproval,
    // unlockDocument is a mutation function, not a dependency
    selectedTeamMembers,
    dealId,
    documentId,
    teamId,
    apolloClient,
    notifyError,
    // notifySuccess, formatMessage, navigate, dealUrl, user are stable - don't need as deps
    documentSource,
    handleSaveDocument,
    possessionTeamName,
    // unlockDocument is a mutation function, not a dependency
    onApproveSuccess,
    currentDocument?.body,
    orderedApprovers,
    useApprovalOrder,
  ]);

  const handleSend = useCallback(async () => {
    return onSubmit();
  }, [onSubmit]);

  const teamMembersIds = teamMembersWithApprovePermission?.map((member) => {
    return member?.id;
  });
  const dealMembersIds = dealMembers.filter(({ id }) => teamMembersIds?.includes(id)).map(({ id }) => id);

  const teamMembersWithApprovePermissionAndAccessToDeal = teamMembersWithApprovePermission.filter(
    (member: { id: string }) => dealMembersIds.includes(member.id)
  );

  // Filter out current user to check if other team members are available
  const otherTeamMembersWithApprovePermissionAndAccessToDeal = teamMembersWithApprovePermissionAndAccessToDeal.filter(
    (member: { id: string }) => member.id !== user.id
  );

  const navigateToManageTeamPage = () => {
    // Open People page in new tab for team management
    const peoplePageUrl = fillPlaceholders(paths.team.landing, { teamId });
    window.open(peoplePageUrl, '_blank');

    // Close the modal after opening new tab
    onClose();
  };

  const noActiveApproversMessage = () => {
    return (
      <span>
        There are no team members with access to this deal.{' '}
        <button className={styles.manageDealTeamLink} onClick={navigateToManageTeamPage}>
          Manage your team
        </button>{' '}
        to update existing access or invite new team members.
      </span>
    );
  };

  const onModalClose = () => {
    onClose();
  };

  const modalFilters = () => {
    return (
      <div>
        {!otherTeamMembersWithApprovePermissionAndAccessToDeal.length && (
          <Warning title="No Team Members" text={noActiveApproversMessage()} />
        )}
        <div className="mb-3">
          {isReviewOnly ? (
            <FormattedMessage id="lease.template.creator" defaultMessage="Share for internal review." />
          ) : (
            <FormattedMessage
              id="lease.template.creator"
              defaultMessage="Share for internal review and optionally request approval."
            />
          )}
        </div>
        <Dropdown className={styles.teamMembersDropdown__container}>
          <Toggle
            id={`dropdown-select-deals-t${teamId}-u${dealId}`}
            selectedCount={selectedTeamMembers.length}
            header={'Reviewer'}
          />

          <Dropdown.Menu
            className={styles.teamMembersDropdown}
            as={TeamMembersDropdown}
            dropdownId={`dropdown-select-teamMembers-t${teamId}-u${dealId}`}
            currentUserId={user.id}
            dealChecked={(user: { id: string }) => {
              return selectedTeamMembers?.includes(user.id);
            }}
            dealValue={(user: { id: string }) => user.id}
            onDealChange={async (event: ChangeEvent<HTMLInputElement>) => {
              const checkbox = event.currentTarget;
              const userId = event.currentTarget.value;

              if (checkbox.checked) {
                setSelectedTeamMembers([...selectedTeamMembers, userId]);
                // Add to ordered approvers if using approval order
                if (useApprovalOrder) {
                  const newApprover = teamMembersWithApprovePermissionAndAccessToDeal.find((m: any) => m.id === userId);
                  if (newApprover) {
                    setOrderedApprovers([...orderedApprovers, newApprover]);
                  }
                }
              } else {
                setSelectedTeamMembers([...pull(selectedTeamMembers, userId)]);
                // Remove from ordered approvers
                if (useApprovalOrder) {
                  setOrderedApprovers(orderedApprovers.filter((a: any) => a.id !== userId));
                }
              }
            }}
          >
            {teamMembersWithApprovePermissionAndAccessToDeal}
          </Dropdown.Menu>
        </Dropdown>
        {!isReviewOnly && (
          <>
            <Row>
              <Col lg={8}>
                <div className={styles.setApprovalRequirementToggle}>
                  <span className={styles.setApprovalRequirementToggle__label}>
                    <FormattedMessage id={RequestApproval.id} defaultMessage={RequestApproval.defaultMessage} />
                  </span>
                  <PillToggle
                    checked={isRequestApproval}
                    isCustomDisabledState={false}
                    onChange={() => {
                      setIsRequestApproval(!isRequestApproval);
                      setIsFullApprovementRequired(false);
                      setIsEditingPermittedDuringApproval(false);
                    }}
                    disabled={!teamMembersWithApprovePermissionAndAccessToDeal.length}
                  />
                </div>
              </Col>
            </Row>
            {isRequestApproval && selectedTeamMembers.length > 1 && (
              <Row>
                <Col lg={8}>
                  <div className={styles.setApprovalRequirementToggle}>
                    <span className={styles.setApprovalRequirementToggle__label}>
                      <FormattedMessage
                        id={SetApprovalRequirement.id}
                        defaultMessage={SetApprovalRequirement.defaultMessage}
                      />
                    </span>
                    <PillToggle
                      checked={isFullApprovementRequired}
                      isCustomDisabledState={false}
                      onChange={() => {
                        setIsFullApprovementRequired(!isFullApprovementRequired);
                        // Only disable editing when switching to parallel mode (no sequential order)
                        if (!isFullApprovementRequired && !useApprovalOrder) {
                          setIsEditingPermittedDuringApproval(false);
                        }
                        // Reset approval order when toggling off "require all"
                        if (isFullApprovementRequired) {
                          setUseApprovalOrder(false);
                        }
                      }}
                      disabled={!teamMembersWithApprovePermissionAndAccessToDeal.length}
                    />
                  </div>
                </Col>
              </Row>
            )}
            {isRequestApproval && isFullApprovementRequired && selectedTeamMembers.length > 1 && (
              <Row>
                <Col lg={8}>
                  <div className={styles.setApprovalRequirementToggle}>
                    <span className={styles.setApprovalRequirementToggle__label}>Use approval order</span>
                    <PillToggle
                      checked={useApprovalOrder}
                      isCustomDisabledState={false}
                      onChange={() => {
                        const newValue = !useApprovalOrder;
                        setUseApprovalOrder(newValue);

                        if (newValue) {
                          // Initialize ordered list from selected members
                          const approversList = selectedTeamMembers
                            .map((id) => teamMembersWithApprovePermissionAndAccessToDeal.find((m: any) => m.id === id))
                            .filter(Boolean);
                          setOrderedApprovers(approversList);
                        } else {
                          // When switching from sequential to parallel, disable editing to prevent conflicts
                          setIsEditingPermittedDuringApproval(false);
                        }
                      }}
                      disabled={!isFullApprovementRequired}
                    />
                  </div>
                </Col>
              </Row>
            )}
            {isRequestApproval && (
              <Row>
                <Col lg={8}>
                  <div className={styles.setApprovalRequirementToggle}>
                    <span className={styles.setApprovalRequirementToggle__label}>
                      <FormattedMessage
                        id={PermitEditingDuringApproval.id}
                        defaultMessage={PermitEditingDuringApproval.defaultMessage}
                      />
                    </span>
                    <PillToggle
                      checked={isEditingPermittedDuringApproval}
                      isCustomDisabledState={false}
                      onChange={() => setIsEditingPermittedDuringApproval(!isEditingPermittedDuringApproval)}
                      disabled={
                        !teamMembersWithApprovePermissionAndAccessToDeal.length ||
                        (isFullApprovementRequired && !useApprovalOrder)
                      }
                    />
                  </div>
                </Col>
              </Row>
            )}
            {isRequestApproval && useApprovalOrder && orderedApprovers.length > 1 && (
              <Row>
                <Col lg={12}>
                  <ApprovalOrderList approvers={orderedApprovers} onReorder={setOrderedApprovers} isDisabled={false} />
                </Col>
              </Row>
            )}
          </>
        )}
      </div>
    );
  };

  const hasRequiredPlaceholders = placeholdersToComplete !== 0;

  const [showWarningModal, setShowWarningModal] = useState(isOpen && hasRequiredPlaceholders);

  const [showModal, setShowModal] = useState(isOpen && !hasRequiredPlaceholders);

  const handleWarningModalClose = () => {
    setShowWarningModal(false);
    setShowModal(false);
    onModalClose();
  };

  useEffect(() => {
    setShowWarningModal(isOpen && hasRequiredPlaceholders);

    setShowModal(isOpen && !hasRequiredPlaceholders);
  }, [isOpen, hasRequiredPlaceholders]);

  return (
    <>
      <RequestApprovalSlideout
        isOpen={showModal}
        onClose={onModalClose}
        modalFilters={modalFilters()}
        onSubmit={handleSend}
        submitDisabled={!selectedTeamMembers?.length}
        optionalMessageInputRef={optionalMessageInputRef}
      />
      <PlaceholderWarningModal
        isOpen={showWarningModal}
        placeholders={placeholdersToComplete}
        onContinue={() => {
          setShowWarningModal(false);
          // Delay modal opening to ensure warning modal closes first
          setTimeout(() => {
            setShowModal(true);
          }, 100);
        }}
        onReview={handleWarningModalClose}
        onHide={handleWarningModalClose}
      />
    </>
  );
};

export default RequestInternalApprovalModalContainer;
